// The sounds of one map: what the followed player does (session sound events),
// other players' footsteps and landings (derived on the client from their
// movetype, like the footstep cycle of PM_Footsteps), target_speakers (looped,
// used by triggers, auto triggering like CG_Speaker) and the start / stop
// sounds of doors, plats and buttons.

import type { VirtualFS } from '../fs/vfs';
import { MASK_WATER } from '../shared/surfaceflags';
import { ANIM_EVENT, MOVETYPE } from '../shared/game/movetype';
import { FOOTSTEP, PLAYER_SOUNDS, footstepFile, footstepForSurface, landFile, playerSoundFiles } from '../shared/game/moveSounds';
import { mapSoundDefs, mapSoundFiles, type MapSoundDefs, type MoverSounds, type SpeakerDef } from '../shared/game/mapSounds';
import type { MoverSet } from '../shared/game/movers';
import type { MapLogic } from '../shared/game/mapLogic';
import type { SessionSound } from '../shared/game/session';
import { loadSoundScripts, noiseFiles, type SoundScripts } from '../shared/soundScript';
import type { Vec3 } from '../shared/math';
import type { PhysicsWorld } from '../shared/world';
import type { Listener, SoundOrigin, SoundSystem } from './soundSystem';

/** Files a map can provide for these events (ET has none: silent then, like ET). */
export const JUMPPAD_SOUND = 'sound/world/jumppad.wav';
export const TELEPORT_SOUND = 'sound/world/teleport.wav';

/** Other players: no footsteps further away than this (they would be silent anyway). */
const REMOTE_STEP_RANGE = 1400;

interface RemoteSteps {
  bobCycle: number;
  lastSeen: number;
}

/** Bob speed and whether steps are heard, per movetype (the PM_Footsteps branches). */
function remoteCycle(moveType: number): { bobmove: number; footstep: boolean } | 'reset' | null {
  switch (moveType) {
    case MOVETYPE.RUN:
    case MOVETYPE.RUNBK:
    case MOVETYPE.STRAFELEFT:
    case MOVETYPE.STRAFERIGHT:
      return { bobmove: 0.4, footstep: true };
    case MOVETYPE.WALKCR:
    case MOVETYPE.WALKCRBK:
      return { bobmove: 0.5, footstep: false };
    case MOVETYPE.PRONE:
    case MOVETYPE.PRONEBK:
      return { bobmove: 0.2, footstep: false };
    case MOVETYPE.IDLE:
    case MOVETYPE.IDLECR:
    case MOVETYPE.IDLEPRONE:
      return 'reset';
    default:
      // in the air, coasting, swimming, ladders: the cycle waits
      return null;
  }
}

export class GameAudio {
  private defs: MapSoundDefs;
  private scripts: SoundScripts | null = null;
  /** Looping speakers that are on. */
  private readonly speakersOn = new Set<number>();
  /** Auto triggering speakers: next play time (ms, performance.now()). */
  private readonly speakerTimes = new Map<number, number>();
  /** Sound script name -> group index -> last played (CG_SoundPickOldestRandomSound). */
  private readonly scriptPlayed = new Map<string, number[]>();
  private readonly moverStates = new Map<number, string>();
  private readonly remotes = new Map<number, RemoteSteps>();
  private footstepCnt = 0;
  /** Other players' sounds (settings). */
  otherPlayers = true;
  private disposed = false;

  constructor(
    private readonly sound: SoundSystem,
    private readonly fs: VirtualFS,
    logic: MapLogic,
    private readonly world: PhysicsWorld,
    private readonly map: string,
  ) {
    this.defs = mapSoundDefs(logic.entities);
    for (const sp of this.defs.speakers) {
      if (sp.looped && sp.startOn) this.speakersOn.add(sp.entityNum);
    }
  }

  /** Reads and decodes the player's and the map's sounds (missing files stay silent). */
  async load(): Promise<void> {
    const { scripts } = await loadSoundScripts(this.fs, this.map);
    this.scripts = scripts;
    const files = [...playerSoundFiles(), ...mapSoundFiles(this.defs, scripts), JUMPPAD_SOUND, TELEPORT_SOUND];
    for (const path of files) {
      if (this.disposed) return;
      if (this.sound.known(path)) continue;
      const data = await this.fs.read(path);
      if (data) this.sound.add(path, data);
      else this.sound.markMissing(path);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.sound.stopAll();
    // the next map has its own speaker and mover sounds; player sounds stay decoded
    const keep = new Set(playerSoundFiles());
    this.sound.forget((p) => keep.has(p));
  }

  /** ET picks among four footstep sounds without repeating the last one. */
  private nextFootstep(): number {
    const r = Math.floor(Math.random() * 4);
    if (this.footstepCnt !== r) this.footstepCnt = r;
    else this.footstepCnt = (this.footstepCnt + 1) & 3;
    return this.footstepCnt;
  }

  /** A sound event of a player's session; origin null = the followed player (heard at the listener). */
  sessionSound(s: SessionSound, origin: SoundOrigin = null): void {
    const snd = this.sound;
    switch (s.kind) {
      case 'step':
        snd.play(footstepFile(s.surface, this.nextFootstep()), { origin });
        break;
      case 'splash':
        snd.play(footstepFile(FOOTSTEP.SPLASH, this.nextFootstep()), { origin });
        break;
      case 'land':
        if (s.surface !== FOOTSTEP.TOTAL) snd.play(landFile(s.surface), { origin });
        if (s.hurt) snd.play(PLAYER_SOUNDS.landHurt, { origin });
        break;
      case 'water':
        snd.play(s.event === 'touch' ? PLAYER_SOUNDS.waterIn : s.event === 'under' ? PLAYER_SOUNDS.waterUnder : PLAYER_SOUNDS.waterOut, { origin });
        break;
      case 'jumppad':
        snd.play(JUMPPAD_SOUND, { origin });
        break;
      case 'teleport':
        snd.play(TELEPORT_SOUND, { origin });
        break;
      case 'speaker':
        this.useSpeaker(s.entityNum);
        break;
    }
  }

  /** The file a speaker plays now (sound scripts pick the group played longest ago, then a random file). */
  private speakerFile(sp: SpeakerDef): string | null {
    const script = this.scripts?.get(sp.noise);
    if (!script || !script.groups.length) {
      const files = this.scripts ? noiseFiles(sp.noise, this.scripts) : [];
      return files[0] ?? null;
    }
    const key = script.name.toLowerCase();
    const played = this.scriptPlayed.get(key) ?? script.groups.map(() => 0);
    let g = 0;
    for (let i = 1; i < played.length; i++) if (played[i] < played[g]) g = i;
    played[g] = performance.now();
    this.scriptPlayed.set(key, played);
    const group = script.groups[g];
    return group.length ? group[Math.floor(Math.random() * group.length)] : null;
  }

  private speakerGlobal(sp: SpeakerDef): boolean {
    return !!this.scripts?.get(sp.noise)?.global;
  }

  /** Use_Target_Speaker: looped ones toggle, others play once (on the player for ACTIVATOR speakers). */
  private useSpeaker(entityNum: number): void {
    const sp = this.defs.speakers.find((s) => s.entityNum === entityNum);
    if (!sp) return;
    if (sp.looped) {
      if (this.speakersOn.has(entityNum)) this.speakersOn.delete(entityNum);
      else this.speakersOn.add(entityNum);
      return;
    }
    const file = this.speakerFile(sp);
    if (file) this.sound.play(file, { origin: sp.activator ? null : sp.origin, volume: sp.volume, range: sp.range, category: 'ambient', global: this.speakerGlobal(sp) });
  }

  /** Another player's footstep cycle, once per drawn frame. */
  remoteFrame(id: number, origin: Vec3, moveType: number, frametime: number, now: number): void {
    let st = this.remotes.get(id);
    if (!st) this.remotes.set(id, (st = { bobCycle: 0, lastSeen: now }));
    st.lastSeen = now;
    const cycle = remoteCycle(moveType);
    if (cycle === null) return;
    if (cycle === 'reset') {
      st.bobCycle = 0;
      return;
    }
    const old = st.bobCycle;
    st.bobCycle = Math.trunc(old + cycle.bobmove * frametime) & 255;
    if (!(((old + 64) ^ (st.bobCycle + 64)) & 128) || !this.otherPlayers) return;
    const l = this.listenerOrigin;
    if (Math.hypot(origin[0] - l[0], origin[1] - l[1], origin[2] - l[2]) > REMOTE_STEP_RANGE) return;
    // feet in water splash, otherwise the ground under them decides
    const feet: Vec3 = [origin[0], origin[1], origin[2] - 23];
    if (this.world.pointContents(feet) & MASK_WATER) {
      this.sound.play(footstepFile(FOOTSTEP.SPLASH, this.nextFootstep()), { origin: [...origin] });
      return;
    }
    if (!cycle.footstep) return;
    const surface = this.surfaceUnder(origin);
    if (surface !== FOOTSTEP.TOTAL) this.sound.play(footstepFile(surface, this.nextFootstep()), { origin: [...origin] });
  }

  /** Another player's animation event (a hard landing makes the landing sound). */
  remoteEvent(origin: Vec3, event: number): void {
    if (!this.otherPlayers || event !== ANIM_EVENT.LAND) return;
    const surface = this.surfaceUnder(origin);
    if (surface !== FOOTSTEP.TOTAL) this.sound.play(landFile(surface), { origin: [...origin] });
  }

  /** Footstep type of the ground below a player (a short trace down from the feet). */
  private surfaceUnder(origin: Vec3): number {
    const end: Vec3 = [origin[0], origin[1], origin[2] - 40];
    const tr = this.world.trace(origin, [-15, -15, 0], [15, 15, 0], end, 1);
    return tr.fraction < 1 ? footstepForSurface(tr.surfaceFlags) : FOOTSTEP.NORMAL;
  }

  private listenerOrigin: Vec3 = [0, 0, 0];

  /**
   * Once per drawn frame: the listener, speakers (loops and auto triggering)
   * and the movers of the followed player at the rendered moment.
   */
  frame(listener: Listener, movers: MoverSet | null, renderTime: number, now: number): void {
    this.listenerOrigin = listener.origin;
    this.sound.setListener(listener);
    for (const sp of this.defs.speakers) {
      if (sp.looped) {
        if (!this.speakersOn.has(sp.entityNum)) continue;
        const file = this.loopFile(sp);
        if (file) this.sound.loop(`spk${sp.entityNum}`, file, { origin: sp.origin, volume: sp.volume, range: sp.range, category: 'ambient' });
        continue;
      }
      // CG_Speaker: only speakers with a random variance trigger on their own
      if (!sp.random) continue;
      const next = this.speakerTimes.get(sp.entityNum);
      if (next !== undefined && now < next) continue;
      if (next !== undefined) {
        const file = this.speakerFile(sp);
        if (file) this.sound.play(file, { origin: sp.origin, volume: sp.volume, range: sp.range, category: 'ambient', global: this.speakerGlobal(sp) });
      }
      this.speakerTimes.set(sp.entityNum, now + sp.wait + sp.random * (Math.random() * 2 - 1));
    }
    if (movers) this.moverSounds(movers, renderTime);
    for (const [id, st] of this.remotes) if (now - st.lastSeen > 2000) this.remotes.delete(id);
    this.sound.update();
  }

  private loopFiles = new Map<number, string | null>();

  /** A looping speaker keeps the file it started with. */
  private loopFile(sp: SpeakerDef): string | null {
    if (!this.loopFiles.has(sp.entityNum)) this.loopFiles.set(sp.entityNum, this.speakerFile(sp));
    return this.loopFiles.get(sp.entityNum) ?? null;
  }

  /** Plays the sound of every mover state change since the last frame. */
  private moverSounds(movers: MoverSet, renderTime: number): void {
    for (const m of this.defs.movers) {
      const i = movers.defs.findIndex((d) => d.entityNum === m.entityNum);
      if (i < 0) continue;
      const state = movers.states[i].state;
      const before = this.moverStates.get(m.entityNum);
      this.moverStates.set(m.entityNum, state);
      if (before === undefined || before === state) continue;
      const file = this.moverFile(m, state);
      if (file) this.sound.play(file, { origin: this.moverCenter(m, movers, renderTime), category: 'ambient' });
    }
  }

  private moverFile(m: MoverSounds, state: string): string | null {
    switch (state) {
      case '1to2':
        return m.start1to2;
      case 'pos2':
        return m.reachedPos2;
      case '2to1':
        return m.start2to1;
      case 'pos1':
        return m.reachedPos1;
    }
    return null;
  }

  /** The middle of the mover's brush model where it is now (CG_SetEntitySoundPosition). */
  private moverCenter(m: MoverSounds, movers: MoverSet, time: number): Vec3 {
    const pose = movers.pose(m.entityNum, time);
    const model = this.world.cm.models[m.model];
    const o = pose?.origin ?? [0, 0, 0];
    if (!model) return [...o];
    return [o[0] + (model.mins[0] + model.maxs[0]) / 2, o[1] + (model.mins[1] + model.maxs[1]) / 2, o[2] + (model.mins[2] + model.maxs[2]) / 2];
  }
}
